// Merchant self-serve profile: edit own contact info + change password
// + send verification messages. Available to every merchant role.

import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError, merchantAuth } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/m/profile')({
  component: MerchantProfile,
});

function MerchantProfile() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['merchant-me'], queryFn: merchantAuth.me });

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');

  // Lazy-init from server data once it's loaded.
  if (me.data && fullName === '' && me.data.full_name) setFullName(me.data.full_name);
  if (me.data && phone === '' && me.data.phone) setPhone(me.data.phone);
  if (me.data && mobile === '' && me.data.mobile) setMobile(me.data.mobile);
  if (me.data && email === '' && me.data.email) setEmail(me.data.email);

  const save = useMutation({
    mutationFn: () =>
      merchantAuth.updateMe({
        full_name: fullName || undefined,
        phone: phone || null,
        mobile: mobile || null,
        email: email || null,
      }),
    onSuccess: () => {
      toast.success(t('merchant.profile.saved', { defaultValue: 'Profile saved.' }));
      qc.invalidateQueries({ queryKey: ['merchant-me'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Save failed'),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('merchant.profile.title', { defaultValue: 'My profile' })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('merchant.profile.subtitle', {
            defaultValue:
              'Update how PayHub reaches you. Email and mobile are used for password recovery.',
          })}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t('merchant.profile.contact', { defaultValue: 'Contact information' })}</CardTitle>
          <CardDescription>
            {t('merchant.profile.verify_hint', {
              defaultValue:
                'Changing email or mobile clears the verified badge until you complete verification.',
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4 max-w-md"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="username">{t('auth.username')}</Label>
              <Input id="username" value={me.data?.username ?? ''} disabled readOnly />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="role">{t('users.col_role', { defaultValue: 'Role' })}</Label>
              <div>
                <Badge variant="secondary" className="uppercase text-[10px]">
                  {me.data?.role}
                </Badge>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="full_name">
                {t('merchant.profile.full_name', { defaultValue: 'Full name' })}
              </Label>
              <Input
                id="full_name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">
                {t('merchant.profile.email', { defaultValue: 'Email' })}
                {me.data?.email_verified_at && (
                  <Badge variant="secondary" className="ms-2 text-[10px]">
                    {t('merchant.profile.verified', { defaultValue: 'verified' })}
                  </Badge>
                )}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mobile">
                {t('merchant.profile.mobile', { defaultValue: 'Mobile (E.164)' })}
                {me.data?.mobile_verified_at && (
                  <Badge variant="secondary" className="ms-2 text-[10px]">
                    {t('merchant.profile.verified', { defaultValue: 'verified' })}
                  </Badge>
                )}
              </Label>
              <Input
                id="mobile"
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="+218..."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">
                {t('merchant.profile.phone', { defaultValue: 'Office phone (optional)' })}
              </Label>
              <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending
                ? t('common.saving', { defaultValue: 'Saving…' })
                : t('common.save', { defaultValue: 'Save' })}
            </Button>
          </form>
        </CardContent>
      </Card>

      <ChangePasswordCard mfaEnabled={!!me.data?.mfa_enabled} />
    </div>
  );
}

function ChangePasswordCard({ mfaEnabled }: { mfaEnabled: boolean }) {
  const { t } = useTranslation();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');

  const change = useMutation({
    mutationFn: () =>
      merchantAuth.changePassword({
        old_password: oldPassword,
        new_password: newPassword,
        code: code || undefined,
      }),
    onSuccess: () => {
      toast.success(
        t('merchant.password.changed', { defaultValue: 'Password updated.' }),
      );
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setCode('');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Change failed'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(
        t('merchant.password.mismatch', { defaultValue: 'New passwords do not match.' }),
      );
      return;
    }
    change.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('merchant.password.title', { defaultValue: 'Change password' })}
        </CardTitle>
        <CardDescription>
          {mfaEnabled
            ? t('merchant.password.mfa_required', {
                defaultValue: 'MFA is enabled — enter your authenticator code to confirm.',
              })
            : t('merchant.password.subtitle', {
                defaultValue: 'Choose a strong password (12+ characters).',
              })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4 max-w-md" onSubmit={onSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="old">
              {t('merchant.password.old', { defaultValue: 'Current password' })}
            </Label>
            <Input
              id="old"
              type="password"
              autoComplete="current-password"
              required
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new">
              {t('merchant.password.new', { defaultValue: 'New password (min 12)' })}
            </Label>
            <Input
              id="new"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">
              {t('merchant.password.confirm', { defaultValue: 'Confirm new password' })}
            </Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          {mfaEnabled && (
            <div className="space-y-1.5">
              <Label htmlFor="code">{t('auth.mfa_code')}</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="\d{6,8}"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
          )}
          <Button type="submit" disabled={change.isPending}>
            {change.isPending
              ? t('common.saving', { defaultValue: 'Saving…' })
              : t('merchant.password.submit', { defaultValue: 'Update password' })}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
